import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const BATCH_API_URL = 'https://api.bazaarvoice.com/data/batch.json';

/**
 * @typedef {object} BazaarvoiceReviewStatistics
 * @property {number} TotalReviewCount
 * @property {number} AverageOverallRating
 * @property {number} [RecommendedCount]
 * @property {number} [NotRecommendedCount]
 * @property {{ RatingValue: number, Count: number }[]} [RatingDistribution]
 */

/**
 * @typedef {object} BazaarvoiceReview
 * @property {number} Rating
 * @property {string | null} Title
 * @property {string | null} ReviewText
 * @property {string} SubmissionTime
 * @property {boolean} IsSyndicated
 * @property {{ Name?: string | null, LogoImageUrl?: string | null } | null} [SyndicationSource]
 * @property {Record<string, unknown>} [Badges]
 * @property {Record<string, { Value?: string }>} [ContextDataValues]
 */

/**
 * Bazaarvoice reviews section.
 *
 * Shows the product's latest reviews in the theme's resource-list carousel and a toggle for the
 * full `data-bv-show="reviews"` widget, which bv.js (loaded by the Bazaarvoice app) renders. The
 * reviews come from one Conversations API batch request, made when the section nears the
 * viewport. The carousel markup is cloned from a server-rendered template and filled before it
 * is inserted, so the slideshow initialises once with its final slides. Without a passkey, on a
 * failed request or with no reviews, the full widget shows instead.
 *
 * `open()` lets the rating summary reveal and scroll to all reviews, as production does.
 *
 * @typedef {object} BazaarvoiceReviewsRefs
 * @property {HTMLElement} fallbackHeading - Heading shown until the carousel (with its own heading) renders.
 * @property {HTMLElement} carousel - Container the carousel is inserted into.
 * @property {HTMLTemplateElement} carouselTemplate - The carousel markup with one review card and the stats card.
 * @property {HTMLElement} actions - Wrapper of the "See all reviews" toggle.
 * @property {HTMLButtonElement} toggle - Shows or hides all reviews.
 * @property {HTMLElement} allReviews - Wrapper of the Bazaarvoice reviews widget.
 *
 * @extends {Component<BazaarvoiceReviewsRefs>}
 */
class BazaarvoiceReviewsComponent extends Component {
  requiredRefs = ['fallbackHeading', 'carousel', 'carouselTemplate', 'actions', 'toggle', 'allReviews'];

  #controller = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {Promise<void> | undefined} */
  #loading;

  /** @type {number | undefined} */
  #scrollTimeout;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#handleDocumentClick, { signal: this.#controller.signal });

    if (this.#loading) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#startLoading();
      },
      { rootMargin: '600px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#controller = new AbortController();
    this.#observer?.disconnect();
    clearTimeout(this.#scrollTimeout);

    // An aborted request has to run again when the section is reconnected.
    if (this.dataset.state !== 'ready' && this.dataset.state !== 'fallback') this.#loading = undefined;
  }

  /**
   * Shows all reviews and scrolls to them.
   */
  async open() {
    this.#setExpanded(true);

    // Let the carousel render first (briefly), so it doesn't push all reviews down mid-scroll.
    await Promise.race([this.#startLoading(), new Promise((resolve) => setTimeout(resolve, 1500))]);

    clearTimeout(this.#scrollTimeout);
    this.#scrollTimeout = window.setTimeout(() => {
      this.refs.allReviews.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }, 200);
  }

  /**
   * Shows or hides all reviews.
   */
  toggle() {
    this.#setExpanded(this.refs.toggle.getAttribute('aria-expanded') !== 'true');
  }

  /**
   * @param {boolean} expanded
   */
  #setExpanded(expanded) {
    this.refs.allReviews.hidden = !expanded;
    this.refs.toggle.setAttribute('aria-expanded', String(expanded));
  }

  /**
   * The rating histogram popup's rows and "Read N reviews" button filter or point to the full
   * widget. bv.js appends that popup to <body>, outside the rating summary, so open all reviews
   * from here.
   *
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.bv_modal_component_container :is(.bv_histogram_row_container, .bv_button_buttonFull)')) {
      return;
    }

    this.open();
  };

  #startLoading() {
    this.#observer?.disconnect();
    this.#loading ??= this.#load();

    return this.#loading;
  }

  async #load() {
    const { productId, passkey } = this.dataset;

    this.dataset.state = 'loading';

    if (!productId || !passkey) {
      this.#showFallback();
      return;
    }

    try {
      const response = await fetch(this.#requestUrl(productId, passkey), { signal: this.#controller.signal });

      if (!response.ok) throw new Error(`Bazaarvoice API responded with ${response.status}`);

      const data = await response.json();
      const productsResult = data.BatchedResults?.q0;
      const reviewsResult = data.BatchedResults?.q1;

      if (productsResult?.HasErrors || reviewsResult?.HasErrors) {
        throw new Error('Bazaarvoice API returned errors', { cause: [productsResult?.Errors, reviewsResult?.Errors] });
      }

      /** @type {BazaarvoiceReviewStatistics | undefined} */
      const statistics = productsResult?.Results?.[0]?.FilteredReviewStatistics;
      /** @type {BazaarvoiceReview[]} */
      const reviews = reviewsResult?.Results ?? [];

      if (!statistics?.TotalReviewCount || !reviews.length) {
        this.#showFallback();
        return;
      }

      this.#render(statistics, reviews);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.warn('Bazaarvoice reviews carousel:', error);
      this.#showFallback();
    }
  }

  /**
   * One request for the review statistics (same filters as the rating summary, so the counts
   * match) and the reviews with text for the carousel.
   *
   * @param {string} productId
   * @param {string} passkey
   */
  #requestUrl(productId, passkey) {
    const { contentLocales = 'en*', sort = 'submissiontime:desc', limit = '10' } = this.dataset;
    const params = new URLSearchParams({
      apiversion: '5.5',
      passkey,
      'resource.q0': 'products',
      'filter.q0': `id:eq:${productId}`,
      'stats.q0': 'reviews',
      'filteredstats.q0': 'reviews',
      'filter_reviews.q0': `contentlocale:eq:${contentLocales}`,
      'resource.q1': 'reviews',
      'sort.q1': sort,
      'limit.q1': limit,
    });

    params.append('filter.q1', `productid:eq:${productId}`);
    params.append('filter.q1', `contentlocale:eq:${contentLocales}`);
    params.append('filter.q1', 'isratingsonly:eq:false');

    return `${BATCH_API_URL}?${params}`;
  }

  #showFallback() {
    this.dataset.state = 'fallback';
    this.refs.actions.hidden = true;
    this.#setExpanded(true);
  }

  /**
   * @param {BazaarvoiceReviewStatistics} statistics
   * @param {BazaarvoiceReview[]} reviews
   */
  #render(statistics, reviews) {
    const fragment = /** @type {DocumentFragment} */ (this.refs.carouselTemplate.content.cloneNode(true));
    const scroller = fragment.querySelector('slideshow-slides');
    const reviewSlide = fragment.querySelector('[data-review-card]')?.closest('slideshow-slide');
    const insightsSlide = fragment.querySelector('[data-review-insights]')?.closest('slideshow-slide');

    if (!scroller || !(reviewSlide instanceof HTMLElement)) {
      this.#showFallback();
      return;
    }

    reviewSlide.remove();
    insightsSlide?.remove();

    const slides = reviews.map((review) => this.#createReviewSlide(reviewSlide, review));

    if (insightsSlide instanceof HTMLElement && this.#fillInsights(insightsSlide, statistics)) {
      slides.splice(1, 0, insightsSlide);
    }

    slides.forEach((slide, index) => {
      slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
      slide.setAttribute('aria-hidden', String(index !== 0));
    });
    scroller.append(...slides);

    const heading = fragment.querySelector('[data-reviews-heading]');
    if (heading) heading.textContent = this.#headingText(statistics);

    this.refs.carousel.replaceChildren(fragment);
    this.refs.carousel.hidden = false;
    this.refs.fallbackHeading.hidden = true;
    this.refs.actions.hidden = false;
    this.dataset.state = 'ready';
  }

  /**
   * @param {HTMLElement} template - The review slide from the carousel template.
   * @param {BazaarvoiceReview} review
   * @returns {HTMLElement}
   */
  #createReviewSlide(template, review) {
    const slide = /** @type {HTMLElement} */ (template.cloneNode(true));
    const rating = Math.min(5, Math.max(0, Math.round(Number(review.Rating) || 0)));

    const stars = slide.querySelector('[data-review-stars]');
    stars?.setAttribute('aria-label', (this.dataset.ratingLabel ?? '').replace('[rating]', String(rating)));
    stars?.querySelectorAll('svg').forEach((star, index) => star.classList.toggle('is-filled', index < rating));

    const date = slide.querySelector('[data-review-date]');
    const submitted = new Date(review.SubmissionTime);
    if (date instanceof HTMLTimeElement && !Number.isNaN(submitted.getTime())) {
      date.dateTime = review.SubmissionTime.slice(0, 10);
      date.textContent = this.#dateFormat.format(submitted);
    } else {
      date?.remove();
    }

    const title = slide.querySelector('[data-review-title]');
    if (title instanceof HTMLElement) {
      title.textContent = review.Title?.trim() ?? '';
      title.hidden = !title.textContent;
    }

    const text = slide.querySelector('[data-review-text]');
    if (text) text.textContent = review.ReviewText?.trim() ?? '';

    // Incentive and affiliation badges must always be shown when present (Bazaarvoice policy).
    const badges = slide.querySelector('[data-review-badges]');
    const badgeLabels = [];
    if (review.Badges?.incentivizedReview || review.ContextDataValues?.IncentivizedReview?.Value === 'True') {
      badgeLabels.push(this.dataset.incentivizedLabel);
    }
    if (review.Badges?.staffContributor) badgeLabels.push(this.dataset.staffLabel);
    if (badges instanceof HTMLElement && badgeLabels.length) {
      badges.textContent = badgeLabels.join(' · ');
      badges.hidden = false;
    }

    // Syndicated reviews must show their source name and logo (Bazaarvoice policy).
    const source = review.IsSyndicated ? review.SyndicationSource : null;
    const sourceElement = slide.querySelector('[data-review-source]');
    if (sourceElement instanceof HTMLElement && (source?.Name || source?.LogoImageUrl)) {
      if (source.LogoImageUrl) {
        const logo = new Image(42, 14);
        logo.className = 'bazaarvoice-review-card__source-logo';
        logo.src = source.LogoImageUrl;
        logo.alt = source.Name ? '' : this.#sourceLabel('');
        logo.loading = 'lazy';
        sourceElement.prepend(logo);
      }

      const sourceName = sourceElement.querySelector('[data-review-source-name]');
      if (sourceName) sourceName.textContent = source.Name ? this.#sourceLabel(source.Name) : '';
      sourceElement.hidden = false;
    }

    return slide;
  }

  /**
   * Fills the stats card. Hides any stat that is zero, and the card when both are.
   *
   * @param {HTMLElement} slide
   * @param {BazaarvoiceReviewStatistics} statistics
   * @returns {boolean} Whether the card has a stat to show.
   */
  #fillInsights(slide, statistics) {
    const recommendedCount = statistics.RecommendedCount ?? 0;
    const recommendedTotal = recommendedCount + (statistics.NotRecommendedCount ?? 0);
    const fiveStarCount = statistics.RatingDistribution?.find((entry) => entry.RatingValue === 5)?.Count ?? 0;

    /** @type {Record<string, number>} */
    const shares = {
      recommended: recommendedTotal ? recommendedCount / recommendedTotal : 0,
      five_star: statistics.TotalReviewCount ? fiveStarCount / statistics.TotalReviewCount : 0,
    };
    const percent = new Intl.NumberFormat(this.#locale, { style: 'percent', maximumFractionDigits: 0 });
    let shown = 0;

    for (const item of slide.querySelectorAll('[data-insight]')) {
      const share = shares[/** @type {HTMLElement} */ (item).dataset.insight ?? ''] ?? 0;
      const value = item.querySelector('[data-insight-value]');

      if (share > 0 && value) {
        value.textContent = percent.format(share);
        shown += 1;
      } else {
        item.remove();
      }
    }

    return shown > 0;
  }

  /**
   * @param {BazaarvoiceReviewStatistics} statistics
   */
  #headingText(statistics) {
    const total = statistics.TotalReviewCount;
    const template =
      new Intl.PluralRules(this.#locale).select(total) === 'one' ? this.dataset.headingOne : this.dataset.headingOther;
    const rating = new Intl.NumberFormat(this.#locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
      statistics.AverageOverallRating
    );

    return (template ?? '').replace('[total]', new Intl.NumberFormat(this.#locale).format(total)).replace('[rating]', rating);
  }

  /**
   * @param {string} name
   */
  #sourceLabel(name) {
    return (this.dataset.syndicatedLabel ?? '').replace('[source]', name).trim();
  }

  /** @type {Intl.DateTimeFormat | undefined} */
  #dateFormatCache;

  get #dateFormat() {
    this.#dateFormatCache ??= new Intl.DateTimeFormat(this.#locale, { day: 'numeric', month: 'short', year: '2-digit' });
    return this.#dateFormatCache;
  }

  /**
   * The storefront locale, e.g. en-AU, or the browser default if it isn't valid.
   *
   * @returns {string | undefined}
   */
  get #locale() {
    try {
      return Intl.getCanonicalLocales(this.dataset.formatLocale ?? [])[0];
    } catch {
      return undefined;
    }
  }
}

if (!customElements.get('bazaarvoice-reviews-component')) {
  customElements.define('bazaarvoice-reviews-component', BazaarvoiceReviewsComponent);
}

/**
 * Bazaarvoice rating summary.
 *
 * Wraps the `data-bv-show="rating_summary"` container. A click anywhere in the summary opens
 * the Bazaarvoice reviews section, as the production theme does. The listener is attached in
 * the bubble phase on this element (not through the delegated `on:click` capture handler) so
 * it runs after Bazaarvoice's own handlers inside the summary, preserving production's order.
 */
class BazaarvoiceRatingSummaryComponent extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const reviews = document.querySelector('bazaarvoice-reviews-component');

    if (!(reviews instanceof BazaarvoiceReviewsComponent)) return;

    event.preventDefault();
    reviews.open();
  };
}

if (!customElements.get('bazaarvoice-rating-summary-component')) {
  customElements.define('bazaarvoice-rating-summary-component', BazaarvoiceRatingSummaryComponent);
}
